/**
 * HaeviLogo — the canonical in-app brand mark (haevi.ai rebrand).
 *
 * One SVG source for every nav / header / login surface. The favicon at
 * ``public/favicon.svg`` mirrors the same artwork so the browser tab and
 * in-app chrome always match.
 *
 * Design: a lowercase "h" whose right leg carries a play-button notch in
 * negative space — the name and the product in one glyph. Indigo→coral
 * gradient on an ink chip; full-resolution masters and the social kit live
 * in ``data/brand/haevi/``.
 *
 * The component keeps its historical export name so no import site churns;
 * the rendered brand is haevi.
 */

interface PonponLogoProps {
  /** Pixel size of the rendered square (default 28). */
  size?: number
  /** Render WITHOUT the dark chip background — useful when the surrounding
   *  surface already supplies the dark fill. */
  bare?: boolean
  className?: string
  title?: string
}

export default function PonponLogo({
  size = 28,
  bare = false,
  className,
  title = 'haevi',
}: PonponLogoProps) {
  // Stable id suffix prevents <defs> collision when multiple logos render.
  const uid = `hv-${size}-${bare ? 'b' : 'c'}`
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title}
    >
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#14141f" />
          <stop offset="1" stopColor="#0b0b14" />
        </linearGradient>
        <linearGradient id={`${uid}-h`} x1="17" y1="12" x2="46" y2="52" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#4f46e5" />
          <stop offset="1" stopColor="#f0567a" />
        </linearGradient>
      </defs>

      {!bare && (
        <>
          <rect x="2" y="2" width="60" height="60" rx="16" ry="16" fill={`url(#${uid}-bg)`} />
          <rect
            x="2.5"
            y="2.5"
            width="59"
            height="59"
            rx="15.5"
            ry="15.5"
            fill="none"
            stroke="rgba(255,255,255,0.08)"
          />
        </>
      )}

      {/* lowercase h: stem, arch, right leg — one gradient stroke */}
      <g stroke={`url(#${uid}-h)`} strokeWidth="9" fill="none" strokeLinecap="round">
        <path d="M 21.5 14 L 21.5 50" />
        <path d="M 21.5 36 C 21.5 25.5, 42.5 25.5, 42.5 36 L 42.5 50" />
      </g>
      {/* play-button notch cut into the right leg (chip-ink fill) */}
      <path
        d="M 39 37.5 L 39 48.5 L 47.5 43 Z"
        fill={bare ? '#0b0b14' : `url(#${uid}-bg)`}
      />
    </svg>
  )
}
